import { Chip, Typography } from '@mui/joy';
import { CaretDown } from '@phosphor-icons/react';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { z } from 'zod';

import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

import { useTenantSlug } from '~/features/tenant/helpers';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import QueryResolver from '~/services/api/query-resolver';
import { useWrappedMutation } from '~/services/api/rest-api';
import { TenantService } from '~/services/api/tenant/endpoints';
import { useForm } from '~/utils/forms';

const WorkflowAccessComponent = ({
  user_id,
  allowedChannel,
  mutate,
}: {
  readonly user_id: number;
  readonly allowedChannel: number[];
  readonly mutate: () => void;
}) => {
  const slug = useTenantSlug();
  const channelsQuery = TenantService.endpoints.intakeChannels.useQuery(
    {
      slug: slug,
    },
    { skip: !slug },
  );

  return (
    <div className='flex flex-col gap-2'>
      <Typography level='body-lg' className='font-medium'>
        <FormattedMessage defaultMessage='Workflow Access' />
      </Typography>
      <Typography level='body-sm' className='' color='neutral'>
        <FormattedMessage defaultMessage='Assign which category of report this user can manage' />
      </Typography>
      <div className='flex gap-4'>
        <QueryResolver query={channelsQuery}>
          {(data) => (
            <SelectIntakeChannelAccess
              mutate={mutate}
              defaultValues={{
                user_id: user_id,
                allowed_form_type_ids: allowedChannel,
                intake_channel_choices: data.map((channel) => ({
                  name: channel.name,
                  id: channel.id,
                })),
              }}
            />
          )}
        </QueryResolver>
      </div>
    </div>
  );
};

export default WorkflowAccessComponent;

const workflowAccessFormObject = z.object({
  user_id: z.number(),
  allowed_form_type_ids: z.array(z.number()),
  intake_channel_choices: z.array(
    z.object({
      name: z.string(),
      id: z.number(),
    }),
  ),
});

type WorkflowAccessForm = z.infer<typeof workflowAccessFormObject>;

function SelectIntakeChannelAccess({
  mutate,
  defaultValues,
}: {
  readonly mutate: () => void;
  readonly defaultValues: Partial<WorkflowAccessForm>;
}) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const slug = useTenantSlug();
  const { createToastNotification, openConfirmationModal } =
    useToastNotificationContext();
  const form = useForm({
    schema: workflowAccessFormObject,
    defaultValues: defaultValues,
  });

  const selectedChannels = form.watch('allowed_form_type_ids');
  const channelList = form.watch('intake_channel_choices');

  const updateChannelAccess = useWrappedMutation(
    TenantService.endpoints.updateRoleSettings.useMutation(),
  );

  const onSaveHandler = form.handleSubmit(async (data) => {
    await updateChannelAccess.mutateAsync(
      {
        userId: data.user_id,
        slug: slug,
        payload: {
          allowed_form_type_ids: data.allowed_form_type_ids,
        },
      },
      {
        onSuccess: () => {
          createToastNotification(
            intl.formatMessage({
              defaultMessage: 'Intake Channel Access updated',
            }),
            'success',
          );
          mutate();
        },
        onError: () => {
          createToastNotification(
            intl.formatMessage({ defaultMessage: 'Something went Wrong' }),
            'error',
          );
        },
      },
    );
  });

  return (
    <div className='flex flex-row gap-2'>
      <div className='shrink-0'>
        <Popover
          open={open}
          onOpenChange={(isOpen) => {
            setOpen(isOpen);
            if (!isOpen) {
              openConfirmationModal(
                intl.formatMessage({
                  defaultMessage: 'Confirmation',
                }),
                intl.formatMessage({
                  defaultMessage:
                    'By restricting access, the user will lose access to their currently channels.',
                }),
                intl.formatMessage({
                  defaultMessage: 'Cancel',
                }),
                intl.formatMessage({
                  defaultMessage: 'Apply Changes',
                }),
                () => {
                  onSaveHandler();
                },
              );
            }
          }}
        >
          <PopoverTrigger>
            <Button
              size='sm'
              variant='outlined'
              color='neutral'
              endDecorator={<CaretDown size={16} />}
              loading={updateChannelAccess.state.isLoading}
            >
              <div className='flex flex-row gap-1'>
                <FormattedMessage defaultMessage='Select Channel' />
                <Chip variant='solid' color='primary' size='sm'>
                  <Typography level='body-xs' className='font-bold !text-white'>
                    {selectedChannels.length}
                  </Typography>
                </Chip>
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent side='bottom' align='start' className='z-[9999]'>
            <div>
              {channelList.map((channel) => (
                <div
                  key={channel.id}
                  className='flex flex-row items-center px-2 py-2 gap-2'
                >
                  <Checkbox
                    size='sm'
                    checked={selectedChannels.includes(channel.id)}
                    onChange={(e) => {
                      form.setValue(
                        'allowed_form_type_ids',
                        e.target.checked
                          ? [...selectedChannels, channel.id]
                          : selectedChannels.filter((id) => id !== channel.id),
                      );
                    }}
                  />
                  <Typography level='body-xs'>{channel.name}</Typography>
                </div>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <div className=' flex-1 flex flex-row flex-wrap gap-2'>
        {selectedChannels.map((selectedChannel) => (
          <div
            key={`channel-${selectedChannel}`}
            className='flex flex-row rounded-md px-3 py-2 border-2 border-black '
          >
            <Typography level='body-xs'>
              {
                channelList.find((channel) => channel.id === selectedChannel)
                  ?.name
              }
            </Typography>
          </div>
        ))}
      </div>
    </div>
  );
}
